import {
  KnockFeedProvider,
  KnockProvider,
  useKnockFeed,
} from '@knocklabs/react';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { FC, ReactElement } from 'react';
import { useEffect } from 'react';

import { useMessagesFeedStore } from './useMessagesFeedStore';

// Mirrors Knock's live feed into the zustand store. Rendered inside the Knock
// providers so `useKnockFeed()` resolves; consumers read the store, not Knock.
const KnockFeedBridge: FC<{ children: ReactElement }> = ({ children }) => {
  const { feedClient, useFeedStore } = useKnockFeed();
  const items = useFeedStore((state) => state.items);
  const loading = useFeedStore((state) => state.loading);

  useEffect(() => {
    useMessagesFeedStore
      .getState()
      .setFeed({ items, loading, client: feedClient });
  }, [items, loading, feedClient]);

  return children;
};

export const MessagesProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const { user } = useRisksmartUser();
  const apiKey = getEnv('REACT_APP_KNOCK_PUBLIC_API_KEY', true);
  const feedId = getEnv('REACT_APP_KNOCK_FEED_CHANNEL_ID', true);
  const knockEnabled = Boolean(apiKey && feedId && user?.userId);

  // When Knock isn't configured (e.g. ephemeral/preview envs) keep the store at
  // its inert empty default. Mounting the real KnockFeedProvider would open a
  // websocket with an empty api_key that fails on a loop and floods the console.
  // Also reset on unmount/identity change so the module-global store never
  // serves a previous session's items or a stale feed client.
  useEffect(() => {
    if (!knockEnabled) {
      useMessagesFeedStore.getState().reset();
    }

    return () => useMessagesFeedStore.getState().reset();
  }, [knockEnabled]);

  if (!apiKey || !feedId || !user?.userId) {
    return children;
  }

  return (
    <KnockProvider apiKey={apiKey} userId={user.userId}>
      <KnockFeedProvider
        feedId={feedId}
        defaultFeedOptions={{ tenant: user.orgKey }}
      >
        <KnockFeedBridge>{children}</KnockFeedBridge>
      </KnockFeedProvider>
    </KnockProvider>
  );
};
